import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import {
  Grid, makeStyles,
} from '@material-ui/core';
import { Loader } from '@googlemaps/js-api-loader';
import MarkerWithLabel from '@googlemaps/markerwithlabel';
import { useRecoilState, useRecoilValue } from 'recoil';
import mapTheme from '../../helpers/mapTheme.json';
import {
  generateMarkers, geocodeAddress, sortMarkersByDistance, updateMarkers,
} from '../../helpers/geocoder';
import AppContext from '../../state/AppContext';
import debounce from '../../helpers/misc';
import {
  preSelectedClinicIdState,
  selectedClinicIdState,
  selectedTimeSlotState,
} from '../../state/selected';
import availableClinicIdsState, { clinicsState } from '../../state/apiData';
import { isLocatingState } from '../../state/apiRequestStatus';

const mapOptions = {
  styles: mapTheme,
  mapTypeControl: false,
  fullscreenControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
};
const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: 300,
    '& .icon-location': {
      width: 34,
      height: 34,
      display: 'block',
      fontSize: 34,
      margin: 0,
    },
    '& .icon-location path': {
      fill: theme.palette.primary.main,
    },
    '& .icon-location.icon-location--disabled path': {
      fill: theme.palette.grey[500],
    },
    '& .icon-location.icon-location--active path': {
      fill: theme.palette.grey[900],
    },
    '& .icon-location.icon-location--user path': {
      fill: theme.palette.error.main,
    },
  },
  iconMyLocation: {
    color: theme.palette.primary.main,
  },
  btnMyLocation: {
    position: 'absolute',
    right: 10,
    bottom: 120,
    backgroundColor: theme.palette.common.white,
    padding: 10,
    minWidth: 0,
  },
  mapWrapper: {
    position: 'relative',
  },
  map: {
    backgroundColor: '#000000',
    minHeight: 300,
    width: '100%',
  },
}));

const Map = () => {
  const {
    setFilteredMarkers,
    mapBounds,
    setMapBounds,
    mapAddress,
    isUserSubmittingAddress,
    setIsUserSubmittingAddress,
    userLocationMarker,
    setUserLocationMarker,
    setMapAddress,
    markers,
    setMarkers,
  } = useContext(AppContext);

  const clinics = useRecoilValue(clinicsState);
  const availableClinicIds = useRecoilValue(availableClinicIdsState);
  const [selectedClinicId, setSelectedClinicId] = useRecoilState(selectedClinicIdState);
  const [isLocating, setIsLocating] = useRecoilState(isLocatingState);
  const selectedTimeSlot = useRecoilValue(selectedTimeSlotState);
  const preSelectedClinicId = useRecoilValue(preSelectedClinicIdState);

  const mapElement = useRef(null);
  const [map, setMap] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [googleMaps, setGoogleMaps] = useState(null);
  const [fakeUserLocationMarker, setFakeUserLocationMarker] = useState(null);

  const onBoundsChanged = (m) => () => {
    setMapBounds(m.getBounds());
  };

  const markerClickAction = (c) => {
    if (!selectedTimeSlot) {
      setSelectedClinicId(c.id);
    }
  };

  const filterMarkers = (gm, mp, gMarkers) => {
    if (userLocationMarker) {
      const centerLatLng = userLocationMarker.position;
      const sortedMarkers = sortMarkersByDistance(gm, centerLatLng, gMarkers,
        availableClinicIds, preSelectedClinicId);
      const selectedMarkers = sortedMarkers.slice(0, 3);

      const selectedMarkersWithUserLocation = selectedMarkers.concat([userLocationMarker]);
      const bounds = new gm.LatLngBounds();

      selectedMarkersWithUserLocation.map(
        (selectedMarker) => bounds.extend(selectedMarker.position),
      );

      mp.fitBounds(bounds, 0);
      setMapBounds(bounds);

      // Hack to be sure it's called at the end of everything
      setTimeout(() => {
        setIsUserSubmittingAddress(false);
        setIsLocating(false);
      }, 500);
    }
  };

  const updateUserLocationMarker = (position) => {
    if (userLocationMarker) {
      userLocationMarker.setMap(null);
    }

    if (fakeUserLocationMarker) {
      fakeUserLocationMarker.setMap(null);
    }

    setUserLocationMarker(
      new MarkerWithLabel({
        position,
        icon: ' ',
        map,
        labelContent: '<svg class="demo-icon icon-location icon-location--user" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 2c1.1 0 2 .9 2 2 0 1.11-.9 2-2 2s-2-.89-2-2c0-1.1.9-2 2-2zm0 10c-1.67 0-3.14-.85-4-2.15.02-1.32 2.67-2.05 4-2.05s3.98.73 4 2.05c-.86 1.3-2.33 2.15-4 2.15z"></path></svg>',
        labelAnchor: new googleMaps.Point(-17, -31),
        clinic: null,
        zIndex: 100,
      }),
    );
  };

  const updateFakeUserLocationMarker = (position) => {
    if (userLocationMarker) {
      userLocationMarker.setMap(null);
    }

    if (fakeUserLocationMarker) {
      fakeUserLocationMarker.setMap(null);
    }

    setFakeUserLocationMarker(
      new MarkerWithLabel({
        position,
        icon: ' ',
        map,
        labelContent: '<svg class="demo-icon icon-location icon-location--user" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 2c1.1 0 2 .9 2 2 0 1.11-.9 2-2 2s-2-.89-2-2c0-1.1.9-2 2-2zm0 10c-1.67 0-3.14-.85-4-2.15.02-1.32 2.67-2.05 4-2.05s3.98.73 4 2.05c-.86 1.3-2.33 2.15-4 2.15z"></path></svg>',
        labelAnchor: new googleMaps.Point(-17, -31),
        clinic: null,
        zIndex: 100,
      }),
    );
  };

  const locateUser = () => {
    setMapAddress('');

    navigator.geolocation.getCurrentPosition((position) => {
      updateUserLocationMarker({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    }, () => {
      setIsLocating(false);
    }, {
      maximumAge: Infinity,
      timeout: 5000,
    });
  };

  useEffect(async () => {
    const loader = new Loader({
      apiKey: 'AIzaSyB4lam3rVp-GYpuwI23cFy4k9goUAagY0Y',
      language: 'fr-CA',
      version: '3.59',
      libraries: ['places', 'geometry'],
    });

    loader
      .load()
      .then(async (google) => {
        setGoogleMaps(google.maps);
        const newMap = new google.maps.Map(mapElement.current, mapOptions);
        const newGeocoder = new google.maps.Geocoder();
        setMap(newMap);
        setGeocoder(newGeocoder);
        const generatedMarkers = generateMarkers(
          google.maps, newMap, markerClickAction, clinics, availableClinicIds, selectedClinicId,
        );

        if (mapBounds) {
          newMap.fitBounds(mapBounds, 0);
        } else {
          const bounds = new google.maps.LatLngBounds();
          generatedMarkers.map((marker) => bounds.extend(marker.position));
          setMarkers(generatedMarkers);
          newMap.fitBounds(bounds, 0);
          setMapBounds(bounds);
        }

        google.maps.event.addListener(newMap, 'bounds_changed', debounce(onBoundsChanged(newMap), 300));
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  }, []);
  useEffect(async () => {
    if (isLocating) {
      locateUser();
    }
  }, [isLocating]);

  useEffect(async () => {
    if (map) {
      const mCenter = map.getCenter();
      const mBounds = map.getBounds();

      if (mCenter) {
        // if user dragging the map
        if (!isUserSubmittingAddress && !isLocating) {
          updateFakeUserLocationMarker(mCenter);

          setMapAddress('');

          const sortedMarkers = sortMarkersByDistance(
            googleMaps, mCenter, markers, availableClinicIds, preSelectedClinicId,
          );
          const selectedMarkers = sortedMarkers
            .filter((marker) => mBounds.contains(marker.position));

          setFilteredMarkers(selectedMarkers);

          // if user entering address or locating themselves
        } else if (userLocationMarker) {
          const sortedMarkers = sortMarkersByDistance(
            googleMaps, userLocationMarker.position, markers,
            availableClinicIds, preSelectedClinicId,
          );

          const selectedMarkers = sortedMarkers
            .filter((marker) => mBounds.contains(marker.position));
          setFilteredMarkers(selectedMarkers);
        }
      }
    }
  }, [mapBounds]);

  useEffect(async () => {
    if (map) {
      if (isUserSubmittingAddress) {
        const latLng = await geocodeAddress(geocoder, mapAddress);

        if (!latLng.failed) {
          updateUserLocationMarker(latLng);
        } else {
          // TODO: return error if no result
          setIsUserSubmittingAddress(false);
        }
      }
    }
  }, [isUserSubmittingAddress]);

  useEffect(async () => {
    if (map) {
      filterMarkers(googleMaps, map, markers);
    }
  }, [userLocationMarker]);

  useEffect(async () => {
    if (map) {
      setMarkers(updateMarkers(
        googleMaps, map, markerClickAction, clinics, availableClinicIds, markers, selectedClinicId,
      ));
    }
  }, [selectedClinicId, selectedTimeSlot]);

  const classes = useStyles();
  return (
    <Grid
      className={classes.container}
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
      >
        <div className={classes.mapWrapper}>
          <div className={classes.map} ref={mapElement}>Map</div>
        </div>
      </Grid>
    </Grid>
  );
};

Map.propTypes = {};

export default Map;
